/**
 * Leaderboard API Client
 * Handles all communication with the leaderboard backend
 */

const getServerUrl = () => {
  return process.env.REACT_APP_API_URL || 'http://localhost:4001';
};

/**
 * Fetch leaderboard for a specific period (legacy - makes separate call per gameMode)
 * @param {string} period - Time period: 'weekly', 'monthly', 'ytd', or 'alltime'
 * @param {string} gameMode - Game mode filter: 'all', 'regular', '15s', '30s', '45s', '60s'
 * @returns {Promise<Object>} Leaderboard data with leaderboard array
 * @deprecated Use fetchAllLeaderboardData instead for better performance
 */
export async function fetchLeaderboard(period = 'weekly', gameMode = 'all') {
  try {
    const serverUrl = getServerUrl();
    const url = new URL(`${serverUrl}/api/leaderboard/${period}`);
    if (gameMode && gameMode !== 'all') {
      url.searchParams.set('gameMode', gameMode);
    }
    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching ${period} leaderboard:`, error);
    throw error;
  }
}

/**
 * Fetch ALL game modes for a period in a single API call
 * Returns pre-calculated top 10 for each game mode - no additional API calls needed when switching filters
 * @param {string} period - Time period: 'weekly', 'monthly', 'ytd', or 'alltime'
 * @returns {Promise<Object>} { period, byGameMode: { all, regular, 15s, 30s, 45s, 60s } }
 */
export async function fetchAllLeaderboardData(period = 'weekly') {
  try {
    const serverUrl = getServerUrl();
    const response = await fetch(`${serverUrl}/api/leaderboard/${period}/all`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching ${period} all-modes leaderboard:`, error);
    throw error;
  }
}

/**
 * Fetch the current week's top 10 leaderboard (backwards compatibility)
 * @returns {Promise<Object>} Leaderboard data with topPlayers array
 */
export async function fetchWeeklyLeaderboard() {
  return fetchLeaderboard('weekly');
}

/**
 * Fetch leaderboard statistics
 * @returns {Promise<Object>} Statistics including total entries, archived weeks
 */
export async function fetchLeaderboardStats() {
  try {
    const serverUrl = getServerUrl();
    const response = await fetch(`${serverUrl}/api/leaderboard/stats`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching leaderboard stats:', error);
    throw error;
  }
}

/**
 * Submit a score to the leaderboard
 * @param {string} playerName - Name of the player
 * @param {number} score - Final score
 * @param {string} gameId - Game ID
 * @param {number} playerCount - Number of players in the game
 * @returns {Promise<Object>} Submission result
 */
export async function submitScore(playerName, score, gameId, playerCount) {
  try {
    const serverUrl = getServerUrl();
    const response = await fetch(`${serverUrl}/api/leaderboard/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        playerName,
        score,
        gameId,
        playerCount
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error submitting score to leaderboard:', error);
    throw error;
  }
}

/**
 * Get or set the active leaderboard period in localStorage
 */
export function getActivePeriod() {
  return localStorage.getItem('qwixx_leaderboard_period') || 'weekly';
}

export function setActivePeriod(period) {
  localStorage.setItem('qwixx_leaderboard_period', period);
}

/**
 * Get or set the active game mode filter in localStorage
 */
export function getActiveGameMode() {
  return localStorage.getItem('qwixx_leaderboard_gameMode') || 'all';
}

export function setActiveGameMode(gameMode) {
  localStorage.setItem('qwixx_leaderboard_gameMode', gameMode);
}
